import type { FC } from "react";
import Badge from "../Badge";
import {
  BikeIcon,
  Copy,
  Mail,
  Phone,
  SquareArrowOutUpRight,
  StarIcon,
} from "lucide-react";
import Text from "../Text";
import { Skeleton } from "../ui/skeleton";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getColorKrunshScore } from "@/lib/utils";
import { toast } from "sonner";

type RestaurantInfo = {
  image?: string;
  name: string;
  address: string;
  rating: string;
  platform: string;
  url?: string;
  deliveryTime?: number;
  firstSeen?: string;
  id: string;
  krunshScore?: number;
  phone?: string;
  email?: string;
};

type Props = {
  restaurant?: RestaurantInfo;
  isLoading?: boolean;
};

const RestaurantIntroduction: FC<Props> = ({ restaurant, isLoading }) => {
  const { t } = useTranslation();
  if (isLoading)
    return <Skeleton className="w-[100] h-[120px] rounded-[12px]" />;

  if (!restaurant) return null;

  const isNewRestaurant = restaurant.firstSeen
    ? dayjs().diff(dayjs(restaurant.firstSeen), "week") < 3
    : false;

  return (
    <div className="relative bg-[#FFFFFF] py-[16px] px-[24px] flex gap-[12px] rounded-[12px]">
      <img
        className="w-[80px] h-[80px] aspect-1/1 object-cover rounded-[16px]"
        src={restaurant.image ?? undefined}
        alt={`restaurant image of ${restaurant.name}`}
      />
      <div className="flex flex-col">
        <div className="flex items-center gap-[8px]">
          <img
            width={24}
            height={24}
            className="rounded-[50%]"
            src={`/partner/${restaurant.platform.toLowerCase()}.png`}
            alt={`logo of ${restaurant.platform.toLowerCase()}`}
          />
          {typeof restaurant?.krunshScore === "number" && (
            <div
              className="bg-[white] ml-[-20px] h-[24px] w-[24px] rounded-[50%] flex items-center justify-center"
              style={{
                border: `2px solid ${getColorKrunshScore(restaurant.krunshScore)}`,
              }}
            >
              <Text
                fontSize={12}
                fontWeight={600}
                color={getColorKrunshScore(restaurant.krunshScore)}
              >
                {restaurant.krunshScore}
              </Text>
            </div>
          )}

          {isNewRestaurant ? (
            <Badge>
              <Text fontWeight={600} fontSize={14} className="uppercase">
                {t("new")}
              </Text>
            </Badge>
          ) : (
            <Badge>
              {restaurant.rating === "0" ? (
                <Text fontWeight={600} fontSize={14}>
                  {t("not_rated")}
                </Text>
              ) : (
                <>
                  <StarIcon height={16} width={16} color="#758195" />
                  <Text fontWeight={600} fontSize={14}>
                    {restaurant.rating}
                  </Text>
                </>
              )}
            </Badge>
          )}
          <Badge>
            <BikeIcon height={16} width={16} color="#758195" />
            <Text
              fontWeight={600}
              fontSize={14}
            >{`${restaurant.deliveryTime} min`}</Text>
          </Badge>
        </div>
        <Text className="mt-[12px] mb-[4px]" fontSize={20} fontWeight={600}>
          {restaurant.name}
        </Text>
        <Text fontSize={16} fontWeight={400}>
          {restaurant.address}
        </Text>
        {restaurant?.url && (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={restaurant.url}
            className="flex items-center gap-[8px] mt-[12px]"
          >
            <SquareArrowOutUpRight color="#44546F" height={16} width={16} />
            <Text fontSize={14} fontWeight={600}>
              {t("show_menu")}
            </Text>
          </a>
        )}
        <div className="mt-[12px] flex flex-wrap gap-[8px]">
          {restaurant.phone && (
            <div
              onClick={() => {
                navigator.clipboard.writeText(restaurant.phone!).then(() => {
                  toast.success(t("copied_to_clipboard"));
                });
              }}
              className="min-w-[180px] bg-[#F7F8F9] cursor-pointer rounded-[8px] p-[8px] flex gap-[8px] justify-between items-center"
            >
              <div className="flex gap-[8px] items-center">
                <Phone height={16} width={16} color="#44546F" />
                <Text fontWeight={400} fontSize={12} color="#44546F">
                  {restaurant.phone}
                </Text>
              </div>
              <Copy height={16} width={16} color="#44546F" />
            </div>
          )}
          {restaurant.email && (
            <div
              onClick={() => {
                navigator.clipboard.writeText(restaurant.email!).then(() => {
                  toast.success(t("copied_to_clipboard"));
                });
              }}
              className="min-w-[180px] bg-[#F7F8F9] cursor-pointer rounded-[8px] p-[8px] flex gap-[8px] justify-between items-center"
            >
              <div className="flex gap-[8px] items-center">
                <Mail height={16} width={16} color="#44546F" />
                <Text fontWeight={400} fontSize={12} color="#44546F">
                  {restaurant.email}
                </Text>
              </div>
              <Copy height={16} width={16} color="#44546F" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RestaurantIntroduction;
